import { Dispatch, useRef } from 'react';

import { isEqual } from 'date-fns';

import useHandleTimeSlotChange from '@calendar/hooks/useHandleTimeSlotChange';
import useTimetablesStore from '@calendar/store/useTimetablesStore';
import useEventTypesStore from '@event/store/useEventTypesStore';
import IconCheck from '@ui/components/icons/IconCheck';

import SelectedTimeslotModal from './SelectedTimeSlotModal';

interface SelectedTimeslotProps {
  length: number;
  startDate: Date;
  isSelectedTimeSlotModalOpen: boolean | string;
  setIsSelectedTimeSlotModalOpen: Dispatch<boolean | string>;
}

const SelectedTimeslot = ({
  length,
  startDate,
  isSelectedTimeSlotModalOpen,
  setIsSelectedTimeSlotModalOpen,
}: SelectedTimeslotProps) => {
  const { selectedEventTimeslotLength } = useEventTypesStore();
  const timerRef = useRef<NodeJS.Timeout | undefined>();
  const { timetables } = useTimetablesStore();
  const timetableWithSameStartDate = timetables?.find((timetable) =>
    isEqual(timetable.timeSlotStart, startDate),
  );
  const handleTimeSlotChange = useHandleTimeSlotChange();

  const handleOnMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      setIsSelectedTimeSlotModalOpen(timetableWithSameStartDate?.uuid || false);
    }, 500);

    return () => {
      clearTimeout(timerRef.current);
    };
  };

  const handleOnMouseLeave = () => {
    clearTimeout(timerRef.current);
    setIsSelectedTimeSlotModalOpen(false);
  };

  const handleClick = () => {
    handleTimeSlotChange(startDate, selectedEventTimeslotLength, timetables);

    return () => {
      clearTimeout(timerRef.current);
    };
  };

  const timeSlotWidth = `calc(${length * 36}px - 4px)`;

  return (
    <div
      className="mb-1 ml-1 flex h-12 cursor-pointer items-center justify-center rounded bg-calendar-selected transition-all duration-300 first-of-type:ml-0 motion-reduce:transition-none lg:relative"
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onClick={handleClick}
      style={{ width: timeSlotWidth }}
    >
      {isSelectedTimeSlotModalOpen === timetableWithSameStartDate?.uuid && (
        <SelectedTimeslotModal
          length={length}
          startDate={startDate}
          isTillMidnight={timetableWithSameStartDate.isTillMidnight}
          isFullDay={timetableWithSameStartDate.isFullDay}
          isSelected={true}
        />
      )}
      <IconCheck className="max-w-[14px] fill-brand-white-100" />
    </div>
  );
};

export default SelectedTimeslot;
